import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withModifiers as _withModifiers, vModelText as _vModelText, Fragment as _Fragment, normalizeClass as _normalizeClass, renderList as _renderList, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "py-16 text-center text-gray-400"
}
const _hoisted_2 = {
  key: 1,
  class: "py-16 text-center text-red-500"
}
const _hoisted_3 = {
  key: 2,
  class: "grid grid-cols-1 lg:grid-cols-2 gap-6 items-start"
}
const _hoisted_4 = { class: "space-y-6" }
const _hoisted_5 = {
  key: 0,
  class: "text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2"
}
const _hoisted_6 = { class: "flex items-center gap-3 cursor-pointer select-none" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 1,
  class: "text-xs text-gray-400"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 2,
  class: "flex flex-wrap items-center gap-3 pt-2"
}
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["disabled", "title"]
const _hoisted_13 = {
  key: 0,
  class: "text-emerald-600 text-sm flex items-center gap-1"
}
const _hoisted_14 = {
  key: 1,
  class: "text-red-500 text-sm"
}
const _hoisted_15 = {
  key: 0,
  class: "bg-white rounded-xl border border-gray-200 p-6 space-y-3"
}
const _hoisted_16 = ["disabled", "title"]
const _hoisted_17 = { class: "bg-white rounded-xl border border-gray-200 overflow-hidden" }
const _hoisted_18 = { class: "overflow-x-auto" }
const _hoisted_19 = { class: "w-full text-sm" }
const _hoisted_20 = { class: "px-4 py-3 text-gray-500 whitespace-nowrap" }
const _hoisted_21 = { class: "px-4 py-3 whitespace-nowrap" }
const _hoisted_22 = { class: "px-4 py-3" }
const _hoisted_23 = { class: "block text-xs text-gray-400" }
const _hoisted_24 = { class: "px-4 py-3 text-xs text-gray-600 break-all" }
const _hoisted_25 = { key: 0 }

import { ref, reactive, computed, onMounted } from 'vue'
import { Check, Mail, Search } from 'lucide-vue-next'
import { apiFetch } from '@/utils/apiFetch'
import ChipsInput from '@/components/notifications/ChipsInput.vue'

interface RecentNotification {
  created_at: string
  courrier_id: string
  destinataire: string | null
  mots_cles: string[]
  emails: string[]
  envoye_par: string | null
}

interface ReceptionConfig {
  enabled: boolean
  keywords: string[]
  recipients: string[]
  updated_at: string | null
  updated_by: string | null
  can_edit: boolean
  recent_notifications: RecentNotification[]
}

const URL_BASE = '/api/admin/reception-notifications'


export default /*@__PURE__*/_defineComponent({
  __name: 'ReceptionAtmPanel',
  setup(__props) {

const loading = ref(false)
const loadError = ref<string | null>(null)
const saving = ref(false)
const testing = ref(false)
const verifying = ref(false)
const message = ref<string | null>(null)
const error = ref<string | null>(null)

const canEdit = ref(false)
const updatedAt = ref<string | null>(null)
const updatedBy = ref<string | null>(null)
const recent = ref<RecentNotification[]>([])
const saved = reactive({ enabled: true, keywords: [] as string[], recipients: [] as string[] })
const form = reactive({ enabled: true, keywords: [] as string[], recipients: [] as string[] })

const aVerifier = ref('')
const verdict = ref<{ concerne: boolean; mots_cles: string[] } | null>(null)

const dirty = computed(() => JSON.stringify(form) !== JSON.stringify(saved))

function validerEmail(valeur: string): string | null {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valeur) ? null : `Adresse invalide : ${valeur}`
}

function apply(data: ReceptionConfig) {
  for (const cible of [saved, form]) {
    cible.enabled = data.enabled
    cible.keywords = [...(data.keywords || [])]
    cible.recipients = [...(data.recipients || [])]
  }
  canEdit.value = data.can_edit
  updatedAt.value = data.updated_at
  updatedBy.value = data.updated_by
  recent.value = data.recent_notifications || []
  verdict.value = null
}

function flash(text: string) {
  message.value = text
  setTimeout(() => { message.value = null }, 3000)
}

async function fetchConfig() {
  loading.value = true
  try {
    apply(await apiFetch<ReceptionConfig>(`${URL_BASE}/config/`))
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Erreur de chargement.'
  } finally {
    loading.value = false
  }
}

async function saveConfig() {
  saving.value = true
  error.value = null
  try {
    apply(await apiFetch<ReceptionConfig>(`${URL_BASE}/config/`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: form.enabled, keywords: form.keywords, recipients: form.recipients }),
    }))
    flash('Enregistré')
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'enregistrement."
  } finally {
    saving.value = false
  }
}

async function sendTest() {
  testing.value = true
  error.value = null
  try {
    const data = await apiFetch<{ sent_to: string[]; echecs: string[] }>(`${URL_BASE}/test/`, { method: 'POST' })
    flash(`Email de test envoyé à ${data.sent_to.join(', ')}`)
    if (data.echecs?.length) error.value = `Échec pour : ${data.echecs.join(', ')}`
  } catch (e) {
    error.value = e instanceof Error ? e.message : "Échec de l'envoi."
  } finally {
    testing.value = false
  }
}

async function verifier() {
  verifying.value = true
  error.value = null
  try {
    verdict.value = await apiFetch(`${URL_BASE}/verifier/`, {
      method: 'POST',
      body: JSON.stringify({ destinataire: aVerifier.value }),
    })
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Vérification impossible.'
  } finally {
    verifying.value = false
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

onMounted(fetchConfig)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("section", null, [
    _cache[16] || (_cache[16] = _createElementVNode("p", { class: "text-sm text-gray-500 mb-5" }, " Quand le secrétariat envoie un courrier reçu, eCable cherche ces mots-clés dans son destinataire (Présidence, Président, Première Dame…) et prévient l'ATM par email. ", -1)),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, "Chargement…"))
      : (loadError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(loadError.value), 1))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("form", {
                onSubmit: _withModifiers(saveConfig, ["prevent"]),
                class: "bg-white rounded-xl border border-gray-200 p-6 space-y-5"
              }, [
                (!canEdit.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_5, " Lecture seule : seul l'administrateur global peut modifier ces réglages. "))
                  : _createCommentVNode("", true),
                _createElementVNode("label", _hoisted_6, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.enabled) = $event)),
                    disabled: !canEdit.value,
                    class: "w-4 h-4"
                  }, null, 8, _hoisted_7), [
                    [_vModelCheckbox, form.enabled]
                  ]),
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-700" }, "Notification activée", -1))
                ]),
                _createElementVNode("div", null, [
                  _cache[5] || (_cache[5] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Mots-clés du destinataire", -1)),
                  _createVNode(ChipsInput, {
                    modelValue: form.keywords,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.keywords) = $event)),
                    disabled: !canEdit.value,
                    placeholder: "Ajouter un mot ou une expression, puis Entrée"
                  }, null, 8, ["modelValue", "disabled"]),
                  _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-xs text-gray-400 mt-1" }, " La casse et les accents sont ignorés, et un mot suffit à déclencher : « président » trouve aussi « Présidente » et « Vice-président ». ", -1))
                ]),
                _createElementVNode("div", null, [
                  _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-semibold text-gray-700 mb-1" }, "Adresses à notifier", -1)),
                  _createVNode(ChipsInput, {
                    modelValue: form.recipients,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.recipients) = $event)),
                    disabled: !canEdit.value,
                    validate: validerEmail,
                    placeholder: "atm@exemple.bj"
                  }, null, 8, ["modelValue", "disabled"]),
                  _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-xs text-gray-400 mt-1" }, " Vide : aucun email n'est envoyé. Ces adresses n'ont pas besoin d'être des comptes eCable. ", -1))
                ]),
                (updatedAt.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                      _createTextVNode(" Dernière modification le " + _toDisplayString(formatDate(updatedAt.value)), 1),
                      (updatedBy.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_9, " par " + _toDisplayString(updatedBy.value), 1))
                        : _createCommentVNode("", true),
                      _cache[9] || (_cache[9] = _createTextVNode(". "))
                    ]))
                  : _createCommentVNode("", true),
                (canEdit.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createElementVNode("button", {
                        type: "submit",
                        disabled: saving.value || !dirty.value,
                        class: "px-5 py-2.5 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
                      }, _toDisplayString(saving.value ? 'Enregistrement…' : 'Enregistrer'), 9, _hoisted_11),
                      _createElementVNode("button", {
                        type: "button",
                        onClick: sendTest,
                        disabled: testing.value || dirty.value || saved.recipients.length === 0,
                        class: "px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50",
                        title: dirty.value ? 'Enregistrez d\'abord vos modifications' : saved.recipients.length === 0 ? 'Enregistrez d\'abord au moins une adresse' : ''
                      }, [
                        _createVNode(_unref(Mail), { class: "w-4 h-4" }),
                        _createTextVNode(_toDisplayString(testing.value ? 'Envoi…' : 'Envoyer un email de test'), 1)
                      ], 8, _hoisted_12),
                      (message.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                            _createVNode(_unref(Check), { class: "w-4 h-4" }),
                            _createTextVNode(" " + _toDisplayString(message.value), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (error.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(error.value), 1))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ], 32),
              (canEdit.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "text-sm font-semibold text-gray-700" }, "Vérifier un destinataire", -1)),
                    _cache[12] || (_cache[12] = _createElementVNode("p", { class: "text-xs text-gray-400" }, " Saisissez un destinataire tel qu'il apparaît sur un courrier pour voir si l'ATM serait prévenu. Aucun email n'est envoyé. ", -1)),
                    _createElementVNode("form", {
                      onSubmit: _withModifiers(verifier, ["prevent"]),
                      class: "flex gap-2"
                    }, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((aVerifier).value = $event)),
                        type: "text",
                        placeholder: "Ex. Cabinet de la Première Dame",
                        class: "flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm",
                        "data-test": "verif-input"
                      }, null, 512), [
                        [_vModelText, aVerifier.value]
                      ]),
                      _createElementVNode("button", {
                        type: "submit",
                        disabled: verifying.value || dirty.value || !aVerifier.value.trim(),
                        class: "px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50",
                        title: dirty.value ? 'Enregistrez d\'abord vos modifications' : ''
                      }, [
                        _createVNode(_unref(Search), { class: "w-4 h-4" }),
                        _cache[10] || (_cache[10] = _createTextVNode("Vérifier "))
                      ], 8, _hoisted_16)
                    ], 32),
                    (verdict.value)
                      ? (_openBlock(), _createElementBlock("p", {
                          key: 0,
                          class: _normalizeClass(["text-sm rounded-lg px-3 py-2", verdict.value.concerne ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-50 text-gray-600']),
                          "data-test": "verdict"
                        }, [
                          (verdict.value.concerne)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(" L'ATM serait prévenu — mot-clé reconnu : " + _toDisplayString(verdict.value.mots_cles.join(', ')) + ". ", 1)
                              ], 64))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Aucun mot-clé reconnu : l'ATM ne serait pas prévenu.")
                              ], 64))
                        ], 2))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_17, [
              _cache[15] || (_cache[15] = _createElementVNode("div", { class: "px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700" }, " Dernières notifications envoyées ", -1)),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("table", _hoisted_19, [
                  _cache[14] || (_cache[14] = _createElementVNode("thead", null, [
                    _createElementVNode("tr", { class: "bg-gray-50 text-left text-gray-500 text-xs uppercase tracking-wide" }, [
                      _createElementVNode("th", { class: "px-4 py-3 font-semibold whitespace-nowrap" }, "Date"),
                      _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Courrier"),
                      _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Destinataire"),
                      _createElementVNode("th", { class: "px-4 py-3 font-semibold" }, "Notifié")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recent.value, (n, idx) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: idx,
                        class: "border-t border-gray-100 align-top"
                      }, [
                        _createElementVNode("td", _hoisted_20, _toDisplayString(formatDate(n.created_at)), 1),
                        _createElementVNode("td", _hoisted_21, [
                          _createVNode(_component_router_link, {
                            to: `/audit/courrier/${n.courrier_id}`,
                            class: "text-[#22282F] underline hover:no-underline"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(" #" + _toDisplayString(n.courrier_id), 1)
                            ]),
                            _: 2
                          }, 1032, ["to"])
                        ]),
                        _createElementVNode("td", _hoisted_22, [
                          _createTextVNode(_toDisplayString(n.destinataire) + " ", 1),
                          _createElementVNode("span", _hoisted_23, _toDisplayString(n.mots_cles.join(', ')), 1)
                        ]),
                        _createElementVNode("td", _hoisted_24, _toDisplayString(n.emails.join(', ')), 1)
                      ]))
                    }), 128)),
                    (recent.value.length === 0)
                      ? (_openBlock(), _createElementBlock("tr", _hoisted_25, _cache[13] || (_cache[13] = [
                          _createElementVNode("td", {
                            colspan: "4",
                            class: "px-4 py-10 text-center text-gray-400"
                          }, "Aucune notification envoyée.", -1)
                        ])))
                      : _createCommentVNode("", true)
                  ])
                ])
              ])
            ])
          ]))
  ]))
}
}

})